import React, { useState } from 'react';
import { Button } from '@components/common/button';
import { Card } from '@components/common/card';
import { Table, TableColumn } from '@components/common/table';
import { LoadingSpinner } from '@components/common/loading_spinner';
import { generate_client_statement } from '@services/client_service';
import { toast } from 'react-toastify';
import {
  Mail, Phone, MapPin, CreditCard, Building2, Droplets, FileText,
  DollarSign, Calendar, Edit, Trash2, CheckCircle, XCircle,
  AlertCircle, User, ArrowLeft
} from 'lucide-react';
import { Client, Property, ClientActivity, ClientFinancialSummary, ClientConsumptionSummary } from '@/types/client_types';
import { ClientStatement } from './Clientstatement';

interface ClientDetailsProps {
  client: Client | null;
  properties?: Property[];
  activities?: ClientActivity[];
  financial_summary?: ClientFinancialSummary;
  consumption_summary?: ClientConsumptionSummary;
  on_edit?: () => void;
  on_delete?: () => void;
  on_back?: () => void;
  on_record_payment?: () => void;
  is_loading?: boolean;
}

export const ClientDetails: React.FC<ClientDetailsProps> = ({
  client,
  properties = [],
  activities = [],
  financial_summary,
  consumption_summary,
  on_edit,
  on_delete,
  on_back,
  on_record_payment,
  is_loading = false
}) => {
  const [active_tab, set_active_tab] = useState<'overview' | 'properties' | 'activity'>('overview');
  const [show_statement, set_show_statement] = useState(false);
  const [statement_data, set_statement_data] = useState<any>(null);
  const [statement_loading, set_statement_loading] = useState(false);

  // Function to load statement data
  const load_statement = async () => {
    if (!client) return;

    set_statement_loading(true);
    try {
      // Get dates for statement (default to last 3 months)
      const end_date = new Date();
      const start_date = new Date();
      start_date.setMonth(start_date.getMonth() - 3);

      const date_from = start_date.toISOString().split('T')[0];
      const date_to = end_date.toISOString().split('T')[0];

      const data = await generate_client_statement(client.id, date_from, date_to);
      
      console.log(" to implement", financial_summary)
      console.log(" to implement", consumption_summary)
  
  

      // Transform API response to match ClientStatement component format
      const transactions = [
        // Map invoices to transactions
        ...(data as any).invoices.map((inv: any, index: number) => ({
          id: `inv-${index}`,
          date: inv.date,
          type: 'invoice' as const,
          reference: inv.invoice_number,
          description: inv.description,
          debit: parseFloat(inv.amount),
          credit: 0,
          balance: 0, // Will be calculated
          status: 'pending' as const
        })),
        // Map payments to transactions
        ...(data as any).payments.map((pay: any, index: number) => ({
          id: `pay-${index}`,
          date: pay.date,
          type: 'payment' as const,
          reference: pay.reference,
          description: `Payment via ${pay.method}`,
          debit: 0,
          credit: parseFloat(pay.amount),
          balance: 0, // Will be calculated
          status: 'paid' as const
        }))
      ];

      // Sort transactions by date
      transactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Calculate running balance
      let running_balance = parseFloat((data as any).opening_balance);
      transactions.forEach(t => {
        running_balance += t.debit - t.credit;
        t.balance = running_balance;
      });

      set_statement_data({
        transactions,
        opening_balance: parseFloat((data as any).opening_balance),
        closing_balance: parseFloat((data as any).closing_balance),
        period_start: (data as any).period.from,
        period_end: (data as any).period.to
      });

      set_show_statement(true);
    } catch (error) {
      console.error('Error loading statement:', error);
      toast.error('Failed to load client statement');
    } finally {
      set_statement_loading(false);
    }
  };

  if (!client) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto text-medium-gray mb-4" />
          <p className="text-medium-gray">Client not found</p>
          <Button variant="primary" onClick={on_back} className="mt-4">Back to Clients</Button>
        </div>
      </div>
    );
  }

  // Show statement view
  if (show_statement && statement_data) {
    return (
      <ClientStatement
        client={{
          id: client.id,
          name: client.name,
          email: client.email,
          phone_number: client.phone_number,
          address: client.address
        }}
        transactions={statement_data.transactions}
        opening_balance={statement_data.opening_balance}
        closing_balance={statement_data.closing_balance}
        period_start={statement_data.period_start}
        period_end={statement_data.period_end}
        is_loading={statement_loading}
        on_back={() => set_show_statement(false)}
        on_download={() => {
          // TODO: Implement PDF download
          toast.info('PDF download coming soon');
        }}
        on_print={() => {
          window.print();
        }}
        on_email={() => {
          // TODO: Implement email sending
          toast.info('Email feature coming soon');
        }}
      />
    );
  }
  
  if (is_loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading client details..." />
      </div>
    );
  }

  // Stats configuration
  const stats_config = [
    { label: 'Outstanding Balance', value: `KES ${((client as any).outstanding_balance || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-error' },
    { label: 'Active Meters', value: (client as any).meter_count || 0, icon: Droplets },
  ];

  // Contact info configuration
  const contact_info = [
    { label: 'Email Address', value: client.email, icon: Mail, color: 'bg-info' },
    { label: 'Phone Number', value: client.phone_number, icon: Phone, color: 'bg-success' },
    { label: 'ID Number', value: client.id_number, icon: CreditCard, color: 'bg-forest-green' },
    { label: 'Address', value: client.address, icon: MapPin, color: 'bg-warning' },
  ].filter(item => item.value);

  // Activity icons mapping
  const activity_icons: Record<string, React.ReactNode> = {
    payment_received: <DollarSign size={20} className="text-success" />,
    invoice_generated: <FileText size={20} className="text-info" />,
    reading_recorded: <Droplets size={20} className="text-forest-green" />,
    meter_added: <Building2 size={20} className="text-lime-green" />,
    status_changed: <AlertCircle size={20} className="text-warning" />,
  };

  // Property table columns
  const property_columns: TableColumn<Property>[] = [
    {
      key: 'property_name',
      header: 'Property Name',
      render: (property) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-lime-green bg-opacity-20 text-forest-green flex items-center justify-center">
            <Building2 size={20} />
          </div>
          <div>
            <p className="font-medium text-charcoal">{property.property_name}</p>
            <p className="text-xs text-medium-gray">{property.physical_address}</p>
          </div>
        </div>
      )
    },
    { key: 'total_units', header: 'Units', align: 'center' as const },
    {
      key: 'occupied_units',
      header: 'Occupied',
      align: 'center' as const,
      render: (property) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-lime-green text-forest-green font-semibold text-sm">
          {property.occupied_units}
        </span>
      )
    }
  ];

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview' },
    ...(client.client_type === 'LANDLORD' ? [{ id: 'properties', label: `Properties (${properties.length})` }] : []),
    { id: 'activity', label: 'Recent Activity' }
  ];
  
  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white/95 border-b border-forest-green sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="secondary" size="sm" onClick={on_back} icon_left={<ArrowLeft size={18} />}>
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-charcoal">{client.name}</h1>
                <p className="text-sm text-medium-gray mt-0.5">
                  {client.client_type === 'LANDLORD' ? 'Landlord' : 'Tenant'} Account
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="sm" onClick={on_edit} icon_left={<Edit size={18} />}>Edit</Button>
              <Button variant="danger" size="sm" onClick={on_delete} icon_left={<Trash2 size={18} />}>Delete</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Card */}
        <div className="bg-gradient-to-r from-forest-green to-dark-forest rounded-md p-6 text-white">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-lime-green text-forest-green flex items-center justify-center font-bold text-3xl flex-shrink-0">
              {client.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-3">{client.name}</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 rounded-md text-sm font-semibold backdrop-blur-sm">
                  {client.client_type === 'LANDLORD' ? <Building2 size={16} /> : <User size={16} />}
                  {client.client_type}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold ${
                  client.is_active ? 'bg-lime-green text-forest-green' : 'bg-white bg-opacity-20 backdrop-blur-sm'
                }`}>
                  {client.is_active ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  {client.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stats_config.map((stat, index) => (
            <div key={index} className="bg-white p-5 rounded-md border border-light-gray hover:border-forest-green transition-colors">
              <div className="flex items-center gap-2 text-medium-gray mb-2">
                <stat.icon size={18} />
                <p className="text-sm font-medium">{stat.label}</p>
              </div>
              <p className={`text-2xl font-bold ${stat.color || 'text-charcoal'}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <Card title="Contact Information" padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contact_info.map((info, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-md ${info.color} bg-opacity-10 flex items-center justify-center flex-shrink-0`}>
                  <info.icon size={22} />
                </div>
                <div>
                  <p className="text-xs text-medium-gray font-medium mb-1">{info.label}</p>
                  <p className="text-sm font-semibold text-charcoal">{info.value}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs */}
        <div className="bg-white rounded-md border border-light-gray">
          {/* Tab Headers */}
          <div className="border-b border-light-gray px-6 flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => set_active_tab(tab.id as any)}
                className={`py-4 px-2 font-semibold transition-all border-b-2 ${
                  active_tab === tab.id
                    ? 'text-forest-green border-forest-green'
                    : 'text-medium-gray border-transparent hover:text-charcoal'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {active_tab === 'overview' && (
              <div className="space-y-6">
                <p className="text-medium-gray">
                  Account created on {new Date(client.created_at || Date.now()).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-off-white rounded-md p-5 border border-light-gray">
                    <h3 className="text-lg font-bold text-charcoal mb-4">Account Summary</h3>
                    <div className="space-y-3">
                      {[
                        ['Account Type', client.client_type],
                        ['Status', client.is_active ? 'Active' : 'Inactive', client.is_active ? 'text-success' : 'text-error'],
                        ['Total Meters', (client  as any).meter_count || 0]
                      ].map(([label, value, className], index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-medium-gray">{label}:</span>
                          <span className={`font-semibold ${className || 'text-charcoal'}`}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-off-white rounded-md p-5 border border-light-gray">
                    <h3 className="text-lg font-bold text-charcoal mb-4">Quick Actions</h3>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" full_width>
                        Generate Invoice
                      </Button>
                      <Button variant="outline" size="sm" full_width onClick={on_record_payment}>
                        Record Payment
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        full_width
                        onClick={load_statement}
                        icon_left={<FileText size={16} />}
                        disabled={statement_loading}
                      >
                        {statement_loading ? 'Loading...' : 'View Statement'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {active_tab === 'properties' && client.client_type === 'LANDLORD' && (
              properties.length > 0 ? (
                <Table data={properties} columns={property_columns} striped hoverable />
              ) : (
                <div className="text-center py-16">
                  <Building2 size={56} className="mx-auto text-medium-gray mb-4" />
                  <p className="text-medium-gray text-lg mb-2">No properties added yet</p>
                  <p className="text-sm text-medium-gray mb-6">Add your first property to get started</p>
                  <Button variant="primary" size="sm">Add Property</Button>
                </div>
              )
            )}
            
            {active_tab === 'activity' && (
              activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-4 p-4 bg-off-white border border-light-gray rounded-md hover:border-forest-green transition-colors">
                      <div className="flex-shrink-0 mt-1">
                        {activity_icons[activity.activity_type] || <AlertCircle size={20} className="text-medium-gray" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-charcoal">{activity.description}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <p className="text-xs text-medium-gray flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(activity.created_at).toLocaleDateString()}
                          </p>
                          {activity.amount && (
                            <p className="text-sm font-semibold text-forest-green">
                              KES {activity.amount.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Calendar size={56} className="mx-auto text-medium-gray mb-4" />
                  <p className="text-medium-gray text-lg">No recent activity</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};